import React, { useMemo } from "react";
import { ID, Match, TournamentParticipant } from "../../../../types";
import { MatchCardsList } from "../MatchCardsList";
import { useTournamentEvent } from "../TournamentEventContext";

interface MatchesTableProps {
  title: string;
  stageMatches: Map<ID, Match>;
  matches: Match[];
  participantsById: Map<ID, TournamentParticipant>;
  participants: TournamentParticipant[];
  getSlotLabel: (match: Match, slot: 1 | 2) => string;
}

export const MatchesTable: React.FC<MatchesTableProps> = ({
  title,
  stageMatches,
  matches,
  participantsById,
  participants,
  getSlotLabel,
}) => {
  const { enableCalendar } = useTournamentEvent();
  const groupedMatches = useMemo(() => {
    const sortedMatches = [...matches].sort((a, b) => {
      if (!enableCalendar) {
        return (a.order ?? 0) - (b.order ?? 0) || a.id - b.id;
      }

      const aTime = a.scheduled_at ? new Date(a.scheduled_at).getTime() : null;
      const bTime = b.scheduled_at ? new Date(b.scheduled_at).getTime() : null;

      if (aTime != null && bTime != null) {
        return aTime - bTime || (a.order ?? 0) - (b.order ?? 0) || a.id - b.id;
      }
      if (aTime != null) return -1;
      if (bTime != null) return 1;
      return (a.order ?? 0) - (b.order ?? 0) || a.id - b.id;
    });

    return sortedMatches.reduce<Array<{ key: string; title: string; matches: Match[] }>>((groups, match) => {
      if (!enableCalendar) {
        const existingGroup = groups.find((group) => group.key === "matches");
        if (existingGroup) {
          existingGroup.matches.push(match);
        } else {
          groups.push({ key: "matches", title: "Match", matches: [match] });
        }
        return groups;
      }

      const date = match.scheduled_at ? new Date(match.scheduled_at) : null;
      const key = date ? date.toLocaleDateString("en-CA") : "unscheduled";
      const title = date
        ? date.toLocaleDateString("it-IT", {
            weekday: "long",
            day: "2-digit",
            month: "long",
            year: "numeric",
          })
        : "Data da definire";
      const existingGroup = groups.find((group) => group.key === key);
      if (existingGroup) {
        existingGroup.matches.push(match);
      } else {
        groups.push({ key, title, matches: [match] });
      }
      return groups;
    }, []);
  }, [enableCalendar, matches]);

  return (
    <div>
      {title ? <h4 className="font-semibold mb-2">{title}</h4> : null}
      <div className="space-y-4">
        {groupedMatches.map((group) => (
          <section key={group.key}>
            <h5 className="mb-2 text-xs font-semibold uppercase text-gray-500">{group.title}</h5>
            <MatchCardsList
              matches={group.matches}
              stageMatches={stageMatches}
              participantsById={participantsById}
              participants={participants}
              getSlotLabel={getSlotLabel}
            />
          </section>
        ))}
        {matches.length === 0 ? (
          <div className="rounded border border-gray-300 p-3 text-center text-sm text-gray-500">
            Nessun match disponibile.
          </div>
        ) : null}
      </div>
    </div>
  );
};
